import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";
import { LogOut, Home as HomeIcon } from "lucide-react";
import MoolyWidget from "@/components/MoolyWidget";
import NotificationBell from "@/components/NotificationBell";
import GlobalClientAutoJoin from "@/components/GlobalClientAutoJoin";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import FeedbackGate from "@/components/FeedbackGate";
import AppRatingGate from "@/components/AppRatingGate";
import SocialRail from "@/components/SocialRail";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isCounselor = user && user.role === "counselor";
  const isEmployee = user && user.role === "employee";
  const bypassGenerateActive = !!user?.is_lead && sessionStorage.getItem("counseltaria_bypass_generate") === "1";

  // Clear the "Generate" bypass flag the moment admin navigates anywhere
  // outside the generate flow (home + apply), so it never gets stuck on.
  useEffect(() => {
    if (bypassGenerateActive && location.pathname !== "/" && location.pathname !== "/apply") {
      sessionStorage.removeItem("counseltaria_bypass_generate");
    }
  }, [location.pathname, bypassGenerateActive]);

  return (
    <div className="min-h-screen" data-testid="app-root">
      <ImpersonationBanner />
      <header className="sticky top-0 z-40 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex sm:grid sm:grid-cols-3 items-center justify-between gap-2">
          {/* Left — Counselor Apply (desktop/tablet only, keeps mobile header to just 2 groups) */}
          <div className="hidden sm:flex items-center">
            {(!user || bypassGenerateActive) && (
              <Link data-testid="nav-apply" to="/apply" className="btn-outline text-sm">
                Counselor Apply
              </Link>
            )}
          </div>

          {/* Center — Home (hidden when already on the home page) */}
          <div className="flex items-center sm:justify-center">
            {!isHomePage && (
              <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
                <HomeIcon className="w-5 h-5" style={{ color: "var(--brand)" }} />
                <span className="font-serif-display text-xl tracking-tight" style={{ color: "var(--text-primary)" }}>
                  Home
                </span>
              </Link>
            )}
          </div>

          {/* Right — auth / dashboard actions */}
          <nav className="flex items-center justify-end gap-2 text-sm">
            {!user && (
              <Link data-testid="nav-login" to="/login" className="btn-outline">Sign in</Link>
            )}            {user && (
              <>
                {isCounselor ? (
                  <>
                    {user.is_lead && <NotificationBell />}
                    <Link data-testid="nav-counselor" to="/counselor" className="btn-outline">Counselor dashboard</Link>
                  </>
                ) : isEmployee ? (
                  <Link data-testid="nav-employee" to="/employee" className="btn-outline">Employee dashboard</Link>
                ) : (
                  <Link data-testid="nav-dashboard" to="/dashboard" className="btn-outline">My sessions</Link>
                )}
                <button
                  data-testid="nav-logout"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="btn-outline inline-flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> {user.is_lead ? "Admin" : user.name?.split(" ")[0]}
                </button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <GlobalClientAutoJoin />
      <FeedbackGate />
      <AppRatingGate />
      <MoolyWidget />
      <SocialRail />
      <footer className="mt-24 border-t border-[color:var(--border-soft)]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8 text-sm mb-8"
               role="navigation" data-testid="footer-legal-nav" aria-label="Legal">
            <div className="order-2 sm:order-1 flex flex-wrap justify-center sm:justify-end gap-x-6 gap-y-2">
              <Link to="/contact" data-testid="footer-contact-link" className="hover:underline">Contact us</Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:underline">Privacy Policy</Link>
            </div>
            <Link to="/serve" data-testid="footer-serve-link"
              className="order-1 sm:order-2 justify-self-center inline-flex items-center rounded-full px-5 py-2.5 text-xs font-medium text-white transition-transform hover:scale-105"
              style={{ background: "#0EA5E9" }}>
              Serve with us
            </Link>
            <div className="order-3 flex flex-wrap justify-center sm:justify-start gap-x-6 gap-y-2">
              <Link to="/terms" data-testid="footer-terms-link" className="hover:underline">Terms &amp; Conditions</Link>
              <Link to="/refund" data-testid="footer-refund-link" className="hover:underline">Refund &amp; Cancellation</Link>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm pt-6 border-t"
               style={{ color: "var(--text-secondary)", borderColor: "var(--border-soft)" }}>
            <div data-testid="footer-copyright">{BRAND.copyright()}</div>
            <div className="flex flex-wrap items-center gap-6">
              <span>Confidential · HIPAA-conscious</span>
              <span>Tele-sessions worldwide</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

